import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../../../../ui/components/Button";
import { TextField } from "../../../../ui/components/TextField";
import { Checkbox } from "../../../../ui/components/Checkbox";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import { FeatherArrowLeft, FeatherSave } from "@subframe/core";
import { useToast } from "../../../../shared/presentation/components";
import {
  getModulesAndActions,
  getRole,
  updateRole,
} from "../../data/api/rolesApi";
import { RolePermissionMatrix } from "../components/RolePermissionMatrix";

interface Module {
  id: number;
  module_code: string;
  module_name: string;
  actions: Array<{
    id: number;
    action_code: string;
    action_name: string;
  }>;
}

export const EditRolePage: React.FC = () => {
  const navigate = useNavigate();
  const { roleId } = useParams<{ roleId: string }>();
  const { showSuccess, showError } = useToast();

  const [formData, setFormData] = useState({
    role_name: "",
    description: "",
    is_manager_required: false,
  });

  const [modules, setModules] = useState<Module[]>([]);
  const [permissions, setPermissions] = useState<
    Record<string, Record<string, boolean>>
  >({});
  const [reviewPolicies, setReviewPolicies] = useState<
    Record<string, Record<string, boolean>>
  >({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  // Load role data and modules
  useEffect(() => {
    const loadData = async () => {
      if (!roleId) {
        showError("Role ID is required");
        navigate("/admin/dashboard?tab=roles");
        return;
      }

      try {
        setLoadingData(true);

        // Load modules and role data in parallel
        const [modulesResponse, roleResponse] = await Promise.all([
          getModulesAndActions(),
          getRole(parseInt(roleId, 10)),
        ]);

        if (modulesResponse.success && modulesResponse.data) {
          setModules(modulesResponse.data);
        }

        if (roleResponse.success && roleResponse.data) {
          const role = roleResponse.data.role;

          // A derived maker-checker role reached by URL: bounce rather than
          // offer a full-replace save that would clobber the team board's
          // derivation. The role list already hides these.
          if (String(role?.role_name ?? "").startsWith("ZN ")) {
            showError(
              "Derived team roles are managed on Settings → Team assignment, not here.",
            );
            navigate("/admin");
            return;
          }

          const rolePermissions = roleResponse.data.permissions || [];
          const roleReviewPolicies = roleResponse.data.review_policies || {};

          // Set form data
          setFormData({
            role_name: role.role_name || "",
            description: role.description || "",
            is_manager_required:
              role.is_manager_required === 1 ||
              role.is_manager_required === true ||
              String(role.is_manager_required).trim() === "1",
          });

          // Convert permissions array to object format
          // Backend returns: [{ module_code, module_name, actions: { action_code: { action_code, action_name, is_granted } } }]
          const permissionsObj: Record<string, Record<string, boolean>> = {};
          if (Array.isArray(rolePermissions)) {
            rolePermissions.forEach((perm: any) => {
              const moduleCode = perm.module_code;
              if (moduleCode) {
                if (!permissionsObj[moduleCode]) {
                  permissionsObj[moduleCode] = {};
                }
                // perm.actions is an object where keys are action_code
                if (perm.actions && typeof perm.actions === "object") {
                  Object.keys(perm.actions).forEach((actionCode) => {
                    const action = perm.actions[actionCode];
                    // Only add if is_granted is true
                    if (
                      action &&
                      (action.is_granted === true || action.is_granted === 1)
                    ) {
                      permissionsObj[moduleCode][actionCode] = true;
                    }
                  });
                }
              }
            });
          }
          setPermissions(permissionsObj);

          // Convert review policies to object format
          // Backend returns: { module_code: { action_code: true } }
          const reviewPoliciesObj: Record<string, Record<string, boolean>> = {};
          if (roleReviewPolicies && typeof roleReviewPolicies === "object") {
            Object.keys(roleReviewPolicies).forEach((moduleCode) => {
              if (
                roleReviewPolicies[moduleCode] &&
                typeof roleReviewPolicies[moduleCode] === "object"
              ) {
                reviewPoliciesObj[moduleCode] = {};
                Object.keys(roleReviewPolicies[moduleCode]).forEach(
                  (actionCode) => {
                    // If the value is truthy, set it to true
                    if (roleReviewPolicies[moduleCode][actionCode]) {
                      reviewPoliciesObj[moduleCode][actionCode] = true;
                    }
                  }
                );
              }
            });
          }
          setReviewPolicies(reviewPoliciesObj);
        } else {
          showError("Failed to load role data");
          navigate("/admin/dashboard?tab=roles");
        }
      } catch (error: any) {
        showError(error.message || "Failed to load role data");
        navigate("/admin/dashboard?tab=roles");
      } finally {
        setLoadingData(false);
      }
    };

    loadData();
  }, [roleId, navigate, showError]);

  const handlePermissionChange = (
    moduleCode: string,
    actionCode: string,
    granted: boolean
  ) => {
    setPermissions((prev) => {
      const newPermissions = { ...prev };
      if (!newPermissions[moduleCode]) {
        newPermissions[moduleCode] = {};
      }
      if (granted) {
        newPermissions[moduleCode][actionCode] = true;
      } else {
        delete newPermissions[moduleCode][actionCode];
        if (Object.keys(newPermissions[moduleCode]).length === 0) {
          delete newPermissions[moduleCode];
        }
      }
      return newPermissions;
    });

    // If unchecking permission, also remove review policy
    if (!granted) {
      setReviewPolicies((prev) => {
        const newReviewPolicies = { ...prev };
        if (newReviewPolicies[moduleCode]) {
          delete newReviewPolicies[moduleCode][actionCode];
          if (Object.keys(newReviewPolicies[moduleCode]).length === 0) {
            delete newReviewPolicies[moduleCode];
          }
        }
        return newReviewPolicies;
      });
    }
  };

  const handleReviewPolicyChange = (
    moduleCode: string,
    actionCode: string,
    requiresReview: boolean
  ) => {
    setReviewPolicies((prev) => {
      const newReviewPolicies = { ...prev };
      if (!newReviewPolicies[moduleCode]) {
        newReviewPolicies[moduleCode] = {};
      }
      if (requiresReview) {
        newReviewPolicies[moduleCode][actionCode] = true;
      } else {
        delete newReviewPolicies[moduleCode][actionCode];
        if (Object.keys(newReviewPolicies[moduleCode]).length === 0) {
          delete newReviewPolicies[moduleCode];
        }
      }
      return newReviewPolicies;
    });
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.role_name.trim()) {
      newErrors.role_name = "Role name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!roleId) {
      showError("Role ID is required");
      return;
    }

    if (!validate()) {
      return;
    }

    try {
      setIsLoading(true);

      await updateRole({
        role_id: parseInt(roleId, 10),
        role_name: formData.role_name.trim(),
        description: formData.description.trim() || undefined,
        is_manager_required: formData.is_manager_required ? 1 : 0,
        permissions,
        review_policies: formData.is_manager_required ? reviewPolicies : {},
      });

      showSuccess("Role updated successfully");
      navigate("/admin/dashboard?tab=roles");
    } catch (error: any) {
      showError(error.message || "Failed to update role");
    } finally {
      setIsLoading(false);
    }
  };

  if (loadingData) {
    return (
      <DefaultPageLayout>
        <div className="flex items-center justify-center p-8">
          <span className="text-body font-body text-subtext-color">
            Loading role data...
          </span>
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="flex w-full flex-col items-start gap-6 p-8">
        {/* Header */}
        <div className="flex w-full items-center gap-4">
          <Button
            variant="neutral-tertiary"
            icon={<FeatherArrowLeft />}
            onClick={() => navigate("/admin/dashboard?tab=roles")}
          >
            Back
          </Button>
          <div className="flex flex-col items-start gap-1">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Edit Role
            </span>
            <span className="text-body font-body text-subtext-color">
              Update role permissions and review policies
            </span>
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="flex w-full flex-col items-start gap-6"
        >
          {/* Basic Information */}
          <div className="flex w-full flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-default-background p-6">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Basic Information
            </span>

            <div className="flex w-full flex-col gap-4">
              <TextField
                className="h-auto w-full flex-none"
                label="Role Name *"
                helpText={errors.role_name || ""}
                error={!!errors.role_name}
              >
                <TextField.Input
                  name="role_name"
                  type="text"
                  placeholder="Enter role name"
                  value={formData.role_name}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setFormData((prev) => ({
                      ...prev,
                      role_name: event.target.value,
                    }));
                    if (errors.role_name) {
                      setErrors((prev) => ({ ...prev, role_name: "" }));
                    }
                  }}
                  disabled={isLoading}
                />
              </TextField>

              <div className="flex w-full flex-col items-start gap-2">
                <span className="text-body font-body text-default-font">
                  Description
                </span>
                <textarea
                  className="w-full min-h-[80px] rounded-lg border border-solid border-neutral-border bg-default-background px-3 py-2 text-body font-body text-default-font outline-none placeholder:text-neutral-400 focus:border-brand-600"
                  placeholder="Enter role description (optional)"
                  value={formData.description}
                  onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                    setFormData((prev) => ({
                      ...prev,
                      description: event.target.value,
                    }));
                  }}
                  disabled={isLoading}
                  rows={3}
                />
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  checked={formData.is_manager_required}
                  onCheckedChange={(checked) => {
                    setFormData((prev) => ({
                      ...prev,
                      is_manager_required: checked,
                    }));
                    // Clear review policies if manager is not required
                    if (!checked) {
                      setReviewPolicies({});
                    }
                  }}
                  label="Manager Required"
                />
                <span className="text-sm text-subtext-color">
                  If enabled, actions can be marked as requiring review
                </span>
              </div>
            </div>
          </div>

          {/* Permissions */}
          {loadingData || modules.length === 0 ? (
            <div className="flex items-center justify-center p-8">
              <span className="text-body font-body text-subtext-color">
                Loading permissions...
              </span>
            </div>
          ) : (
            <div className="flex w-full flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-default-background p-6">
              <div className="flex w-full flex-col items-start gap-2">
                <span className="text-heading-3 font-heading-3 text-default-font">
                  Permissions
                </span>
                <span className="text-body font-body text-subtext-color">
                  Select the permissions for this role. If "Manager Required" is
                  enabled, you can also specify which actions require review.
                </span>
              </div>

              <RolePermissionMatrix
                modules={modules}
                permissions={permissions}
                reviewPolicies={reviewPolicies}
                isManagerRequired={formData.is_manager_required}
                onPermissionChange={handlePermissionChange}
                onReviewPolicyChange={handleReviewPolicyChange}
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex w-full items-center justify-end gap-3">
            <Button
              variant="neutral-secondary"
              onClick={() => navigate("/admin/dashboard?tab=roles")}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="brand-primary"
              type="submit"
              icon={<FeatherSave />}
              loading={isLoading}
              disabled={isLoading}
            >
              Update Role
            </Button>
          </div>
        </form>
      </div>
    </DefaultPageLayout>
  );
};
